/**
 * AstorActivityTracker
 *
 * An invisible component (renders null) that passively tracks user activity
 * within a portal. It listens to route changes, monitors idle time, and
 * checks for automatic milestone completion based on route visits.
 *
 * Must be mounted inside an <AstorContextProvider>.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { useCallback, useEffect, useRef } from 'react';

import type { AstorActivityTrackerProps } from './astor-context.types';
import { useAstorContext } from './useAstorContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const IDLE_TIMEOUT_MS = 30_000;
const IDLE_EVENTS: Array<keyof DocumentEventMap> = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorActivityTracker({ route }: AstorActivityTrackerProps) {
  const { trackPageView, trackEvent, resetIdle, completeMilestone, onboarding } = useAstorContext();

  const prevRouteRef = useRef<string>('');
  const routeEnterTimeRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start or reset the idle timer
  const startIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      trackEvent('idle_detected');
    }, IDLE_TIMEOUT_MS);
  }, [trackEvent]);

  // Handle user activity (reset idle)
  const handleActivity = useCallback(() => {
    resetIdle();
    startIdleTimer();
  }, [resetIdle, startIdleTimer]);

  // Route change: track page_view and page_leave
  useEffect(() => {
    if (!route) return;

    const now = Date.now();

    // Record page_leave for previous route (with duration)
    if (prevRouteRef.current && prevRouteRef.current !== route) {
      const duration = now - routeEnterTimeRef.current;
      trackEvent('page_leave', {
        route: prevRouteRef.current,
        duration,
      });
    }

    // Record page_view for new route
    if (route !== prevRouteRef.current) {
      trackPageView(route);
      routeEnterTimeRef.current = now;
      prevRouteRef.current = route;
    }

    // Reset idle timer on route change
    startIdleTimer();
  }, [route, trackPageView, trackEvent, startIdleTimer]);

  // Check for automatic milestone completion on route change
  useEffect(() => {
    if (!route || !onboarding.plan || onboarding.dismissed) return;

    for (const section of onboarding.plan.sections) {
      for (const milestone of section.milestones) {
        if (
          milestone.status !== 'completed' &&
          !onboarding.completedMilestones.has(milestone.id) &&
          milestone.completionCriteria.type === 'route_visit' &&
          route.startsWith(milestone.completionCriteria.value)
        ) {
          completeMilestone(milestone.id);
        }
      }
    }
  }, [route, onboarding.plan, onboarding.dismissed, onboarding.completedMilestones, completeMilestone]);

  // Attach/detach idle-reset event listeners on document
  useEffect(() => {
    for (const event of IDLE_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }
    // Start initial idle timer
    startIdleTimer();

    return () => {
      for (const event of IDLE_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [handleActivity, startIdleTimer]);

  // This component is invisible
  return null;
}
